/**
 * V3 account-governance domain contract.
 *
 * The V3 decision belongs to the ACCOUNT — recorded by its designated Lead
 * Administrator — never to an individual user. Persistence, the authoritative
 * write path, and the Lead Administrator authority field are deliberately not
 * chosen here; this module is the persistence-independent contract consumed by
 * later state/UI/backend work.
 *
 * Deliberately separate from legal terms acceptance (`src/app/core/legal/terms.ts`):
 * the Product decision must never be coupled to `termsVersion` or custom claims.
 */

/** Account-scoped V3 Product decision recorded by the designated Lead Administrator. */
export type AccountV3DecisionStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED';

/** Access mode every user of an account inherits from the account's V3 decision. */
export type AccountAccessMode = 'FULL_V3' | 'V2_COMPATIBILITY_MODE';

/** Canonical decision statuses, for runtime validation of untrusted values. */
export const ACCOUNT_V3_DECISION_STATUSES: readonly AccountV3DecisionStatus[] = ['PENDING', 'ACCEPTED', 'DECLINED'];

/**
 * Where a V3 support/info-session request was raised from (86e2dwhcu).
 * Lockstep with SUPPORT_REQUEST_SURFACES in the v4_requestInfoSession
 * function. A request is intent only — it is never a decision and can never
 * change the account's decision or access state.
 */
export type SupportRequestSurface = 'decision-card' | 'accept-prompt' | 'decline-prompt';

/**
 * A provider-confirmed upcoming session booking for the active account
 * (86e2e3ybv slice 2). Stamped onto the request document by the Calendly
 * webhook — the ONLY authoritative booking signal per the FT-09 contract
 * (redirects, success pages, and user assertions never count). Purely
 * informational: display and self-serve cancel/reschedule; booking state can
 * never change the account's decision or access.
 */
export interface UpcomingSessionBooking {
  /** Request document id under .../v3Decision/infoSessionRequests. */
  requestId: string;
  /** ISO start time from the provider. */
  eventStartTime: string;
  /** Provider event name, e.g. "Blume V3 Information Session". */
  eventName: string | null;
  /** The invitee's own self-serve cancel link. */
  cancelUrl: string | null;
  /** The invitee's own self-serve reschedule link. */
  rescheduleUrl: string | null;
}
